import { useState, useEffect, useRef } from 'react'
import { PageHeader, Breadcrumb } from '@/components/layout'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Avatar,
  Badge,
  Skeleton,
  Alert,
  Separator,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui'
import { useApiClient, api, type User, type Session, type MFAFactor, type LinkedIdentity, type TOTPSetupResponse } from '@/api'
import {
  RefreshCw,
  Upload,
  Camera,
  Key,
  Monitor,
  Trash2,
  Smartphone,
  ShieldCheck,
  Copy,
  Download,
  Fingerprint,
  Mail,
  User as UserIcon,
  Lock,
  Shield,
  Link2,
} from 'lucide-react'

// Social providers configuration
const SOCIAL_PROVIDERS = [
  { id: 'google', name: 'Google' },
  { id: 'github', name: 'GitHub' },
  { id: 'discord', name: 'Discord' },
  { id: 'twitter', name: 'X' },
] as const

// Get API URL based on hostname
function getApiUrl(): string {
  const hostname = window.location.hostname

  // Production pattern: account.{slug}.u.software -> api.{slug}.u.software
  const prodMatch = hostname.match(/^account\.(.+)\.u\.software$/)
  if (prodMatch) {
    return `https://api.${prodMatch[1]}.u.software`
  }

  // Fly.io deployment
  if (hostname === 'usoftware-account.fly.dev') {
    return 'https://usoftware-api.fly.dev'
  }

  // Development fallback
  return 'http://localhost:8080'
}

// Social provider icons
function SocialProviderIcon({ provider, className = '' }: { provider: string; className?: string }) {
  const iconClass = `h-4 w-4 ${className}`

  switch (provider) {
    case 'google':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      )
    case 'github':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      )
    case 'discord':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
        </svg>
      )
    case 'twitter':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    default:
      return <div className={`${iconClass} rounded-full bg-[var(--text-muted)]`} />
  }
}

export function AccountPage() {
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // User data
  const [user, setUser] = useState<User | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isRemovingAvatar, setIsRemovingAvatar] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  // Security data
  const [sessions, setSessions] = useState<Session[]>([])
  const [mfaFactors, setMfaFactors] = useState<MFAFactor[]>([])
  const [identities, setIdentities] = useState<LinkedIdentity[]>([])
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Email change modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [isChangingEmail, setIsChangingEmail] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  // Password change modal state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Recovery codes modal state
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [isLoadingCodes, setIsLoadingCodes] = useState(false)

  // TOTP setup modal state
  const [totpModalOpen, setTotpModalOpen] = useState(false)
  const [totpSetup, setTotpSetup] = useState<TOTPSetupResponse | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [totpError, setTotpError] = useState<string | null>(null)
  const [isSettingUpTotp, setIsSettingUpTotp] = useState(false)
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false)

  // Passkey enrollment modal state
  const [passkeyModalOpen, setPasskeyModalOpen] = useState(false)
  const [passkeyName, setPasskeyName] = useState('')
  const [passkeyError, setPasskeyError] = useState<string | null>(null)
  const [isEnrollingPasskey, setIsEnrollingPasskey] = useState(false)

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [userData, sessionsRes, mfaRes, identitiesRes] = await Promise.all([
        api.getMe(client),
        api.getSessions(client),
        api.getMFAFactors(client),
        api.getIdentities(client),
      ])
      setUser(userData)
      setDisplayName(userData.display_name || '')
      setUsername(userData.username || '')
      setSessions(sessionsRes)
      setMfaFactors(mfaRes)
      setIdentities(identitiesRes)
    } catch (err) {
      console.error('Failed to load account data:', err)
      setError('Failed to load account data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Profile handlers
  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const updated = await api.updateMe(client, {
        display_name: displayName || undefined,
        username: username || undefined,
      })
      setUser(updated)
      setDisplayName(updated.display_name || '')
      setUsername(updated.username || '')
      setSuccess('Profile updated successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      console.error('Failed to save profile:', err)
      setError(err.message || 'Failed to save profile')
    } finally {
      setIsSaving(false)
    }
  }

  const hasProfileChanges = displayName !== (user?.display_name || '') ||
    username !== (user?.username || '')

  const handleRemoveAvatar = async () => {
    setIsRemovingAvatar(true)
    setError(null)
    try {
      const updated = await api.removeAvatar(client)
      setUser(updated)
      setSuccess('Avatar removed successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to remove avatar:', err)
      setError('Failed to remove avatar')
    } finally {
      setIsRemovingAvatar(false)
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploadingAvatar(true)
    setError(null)
    try {
      await api.uploadAvatar(client, file)
      await loadData()
      setSuccess('Avatar updated successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to upload avatar:', err)
      setError('Failed to upload avatar')
    } finally {
      setIsUploadingAvatar(false)
      if (avatarInputRef.current) {
        avatarInputRef.current.value = ''
      }
    }
  }

  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) {
      setEmailError('Please fill in all fields')
      return
    }

    setIsChangingEmail(true)
    setEmailError(null)

    try {
      await api.requestEmailChange(client, {
        new_email: newEmail,
        password: emailPassword,
      })
      setEmailModalOpen(false)
      setNewEmail('')
      setEmailPassword('')
      setSuccess('Verification email sent to your new address. Please check your inbox.')
      setTimeout(() => setSuccess(null), 5000)
    } catch (err: any) {
      console.error('Failed to change email:', err)
      setEmailError(err.message || 'Failed to change email')
    } finally {
      setIsChangingEmail(false)
    }
  }

  // Security handlers
  const handleRevokeSession = async (sessionId: string) => {
    setActionLoading(`session-${sessionId}`)
    try {
      await api.revokeSession(client, sessionId)
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
    } catch (err) {
      console.error('Failed to revoke session:', err)
      setError('Failed to revoke session')
    } finally {
      setActionLoading(null)
    }
  }

  const handleRevokeAllSessions = async () => {
    setActionLoading('revoke-all')
    try {
      await api.revokeAllOtherSessions(client)
      setSessions((prev) => prev.filter((s) => s.is_current))
    } catch (err) {
      console.error('Failed to revoke sessions:', err)
      setError('Failed to revoke sessions')
    } finally {
      setActionLoading(null)
    }
  }

  const handleRemoveMFA = async (factorId: string) => {
    setActionLoading(`mfa-${factorId}`)
    try {
      await api.removeMFAFactor(client, factorId)
      setMfaFactors((prev) => prev.filter((f) => f.id !== factorId))
      setSuccess('MFA factor removed')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to remove MFA factor:', err)
      setError('Failed to remove MFA factor')
    } finally {
      setActionLoading(null)
    }
  }

  const handleUnlinkIdentity = async (identityId: string) => {
    setActionLoading(`identity-${identityId}`)
    try {
      await api.unlinkIdentity(client, identityId)
      setIdentities((prev) => prev.filter((i) => i.id !== identityId))
    } catch (err) {
      console.error('Failed to unlink account:', err)
      setError('Failed to unlink account')
    } finally {
      setActionLoading(null)
    }
  }

  const handleLinkAccount = async (provider: string) => {
    const apiUrl = getApiUrl()
    const returnTo = encodeURIComponent(window.location.href)

    try {
      const response = await fetch(`${apiUrl}/auth/${provider}/redirect?return_to=${returnTo}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.detail || `Failed to connect to ${provider}`)
        return
      }

      const data = await response.json()
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      } else {
        setError('Invalid response from server')
      }
    } catch (err) {
      console.error('Failed to initiate linking:', err)
      setError(`Failed to connect to ${provider}. Please try again.`)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    setIsChangingPassword(true)
    setPasswordError(null)

    try {
      await api.changePassword(client, {
        current_password: currentPassword,
        new_password: newPassword,
      })
      setPasswordModalOpen(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSuccess('Password changed successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      console.error('Failed to change password:', err)
      setPasswordError(err.message || 'Failed to change password')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleRegenerateRecoveryCodes = async () => {
    setIsLoadingCodes(true)
    try {
      const response = await api.regenerateRecoveryCodes(client)
      setRecoveryCodes(response.codes)
    } catch (err) {
      console.error('Failed to regenerate recovery codes:', err)
      setError('Failed to regenerate recovery codes')
    } finally {
      setIsLoadingCodes(false)
    }
  }

  const copyRecoveryCodes = () => {
    const text = recoveryCodes.join('\n')
    navigator.clipboard.writeText(text)
    setSuccess('Recovery codes copied to clipboard')
    setTimeout(() => setSuccess(null), 3000)
  }

  const downloadRecoveryCodes = () => {
    const text = recoveryCodes.join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  // TOTP setup handlers
  const handleBeginTotpSetup = async () => {
    setTotpModalOpen(true)
    setIsSettingUpTotp(true)
    setTotpError(null)
    setTotpSetup(null)
    setTotpCode('')

    try {
      const response = await api.beginTOTPSetup(client)
      setTotpSetup(response)
    } catch (err: any) {
      console.error('Failed to begin TOTP setup:', err)
      setTotpError(err.message || 'Failed to start TOTP setup')
    } finally {
      setIsSettingUpTotp(false)
    }
  }

  const handleVerifyTotp = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setTotpError('Please enter a 6-digit code')
      return
    }

    setIsVerifyingTotp(true)
    setTotpError(null)

    try {
      const factor = await api.verifyTOTPSetup(client, { factor_id: totpSetup!.factor_id, code: totpCode })
      setMfaFactors((prev) => [...prev, factor])
      setTotpModalOpen(false)
      setTotpSetup(null)
      setTotpCode('')
      setSuccess('Authenticator app set up successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      console.error('Failed to verify TOTP:', err)
      setTotpError(err.message || 'Invalid code. Please try again.')
    } finally {
      setIsVerifyingTotp(false)
    }
  }

  // Passkey enrollment handlers
  const handleBeginPasskeyEnrollment = async () => {
    setPasskeyModalOpen(true)
    setPasskeyError(null)
    setPasskeyName('')
  }

  const handleEnrollPasskey = async () => {
    setIsEnrollingPasskey(true)
    setPasskeyError(null)

    try {
      if (!window.PublicKeyCredential) {
        throw new Error('Passkeys are not supported on this device')
      }

      const apiUrl = getApiUrl()
      const optionsResponse = await fetch(`${apiUrl}/auth/webauthn/register/options`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: passkeyName || 'My Passkey' }),
      })

      if (!optionsResponse.ok) {
        const data = await optionsResponse.json()
        throw new Error(data.detail || 'Failed to get registration options')
      }

      const { options } = await optionsResponse.json()

      const publicKeyOptions: PublicKeyCredentialCreationOptions = {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        user: {
          ...options.user,
          id: base64UrlToBuffer(options.user.id),
        },
        excludeCredentials: options.excludeCredentials?.map((cred: any) => ({
          ...cred,
          id: base64UrlToBuffer(cred.id),
        })) || [],
      }

      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      }) as PublicKeyCredential

      if (!credential) {
        throw new Error('Failed to create credential')
      }

      const response = credential.response as AuthenticatorAttestationResponse
      const encodedCredential = {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64Url(response.clientDataJSON),
          attestationObject: bufferToBase64Url(response.attestationObject),
        },
      }

      const verifyResponse = await fetch(`${apiUrl}/auth/webauthn/register/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: encodedCredential,
          name: passkeyName || 'My Passkey',
        }),
      })

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json()
        throw new Error(data.detail || 'Failed to verify credential')
      }

      const mfaRes = await api.getMFAFactors(client)
      setMfaFactors(mfaRes)
      setPasskeyModalOpen(false)
      setPasskeyName('')
      setSuccess('Passkey added successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      console.error('Failed to enroll passkey:', err)
      if (err.name === 'NotAllowedError') {
        setPasskeyError('Passkey enrollment was cancelled or timed out')
      } else {
        setPasskeyError(err.message || 'Failed to enroll passkey')
      }
    } finally {
      setIsEnrollingPasskey(false)
    }
  }

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Account"
          description="Manage your profile and security settings"
          breadcrumb={
            <Breadcrumb items={[{ label: 'Account' }]} />
          }
        />
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-6">
                <Skeleton className="h-24 w-24 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </CardContent>
          </Card>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error && !user) {
    return (
      <div>
        <PageHeader
          title="Account"
          description="Manage your profile and security settings"
        />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{error}</p>
            <Button onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const hasMFA = mfaFactors.filter(f => f.verified).length > 0
  const totpFactor = mfaFactors.find((f) => f.factor_type === 'totp' && f.verified)
  const passkeys = mfaFactors.filter((f) => f.factor_type === 'passkey' && f.verified)
  const currentSession = sessions.find((s) => s.is_current)
  const otherSessions = sessions.filter((s) => !s.is_current)

  return (
    <div>
      <PageHeader
        title="Account"
        description="Manage your profile and security settings"
        breadcrumb={
          <Breadcrumb items={[{ label: 'Account' }]} />
        }
      />

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-6" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="space-y-8">
        {/* Profile Header Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Avatar with upload */}
              <div className="relative group">
                <Avatar
                  src={user?.avatar_url}
                  fallback={user?.display_name || user?.email || '?'}
                  userId={user?.id}
                  size="2xl"
                />
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  {isUploadingAvatar ? (
                    <RefreshCw className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </button>
              </div>

              {/* User info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-0.5">
                  <h2 className="text-xl font-semibold text-[var(--text-primary)] truncate">
                    {user?.display_name || 'No display name'}
                  </h2>
                  {user?.email_verified && (
                    <Badge variant="success" className="shrink-0">Verified</Badge>
                  )}
                </div>
                {user?.username ? (
                  <p className="text-sm text-[var(--text-secondary)]">@{user.username}</p>
                ) : (
                  <p className="text-sm text-[var(--text-muted)] italic">No username set</p>
                )}
                <p className="text-xs text-[var(--text-muted)] mt-2">{user?.email}</p>
              </div>

              {/* Avatar actions */}
              {user?.avatar_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveAvatar}
                  loading={isRemovingAvatar}
                  className="shrink-0"
                >
                  Remove photo
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-[var(--text-secondary)]" />
              <CardTitle>Personal Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Display Name & Username */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                />
                <Input
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="Enter your username"
                  prefix="@"
                />
              </div>
              <Button
                onClick={handleSave}
                loading={isSaving}
                disabled={!hasProfileChanges}
              >
                Save changes
              </Button>

              <Separator />

              {/* Email */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[var(--text-secondary)]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">Email Address</p>
                    <p className="text-sm text-[var(--text-secondary)]">{user?.email}</p>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEmailModalOpen(true)}
                >
                  Change
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[var(--text-secondary)]" />
              <CardTitle>Security</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-[var(--text-secondary)]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">Password</p>
                    <p className="text-sm text-[var(--text-secondary)]">Change your password to keep your account secure</p>
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setPasswordModalOpen(true)}>
                  Change
                </Button>
              </div>

              <Separator />

              {/* Two-Factor Authentication */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-5 w-5 text-[var(--text-secondary)]" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">Two-Factor Authentication</p>
                      <p className="text-sm text-[var(--text-secondary)]">Add an extra layer of security to your account</p>
                    </div>
                  </div>
                  <Badge variant={hasMFA ? 'success' : 'default'}>
                    {hasMFA ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>

                <div className="pl-8 space-y-3">
                  {/* TOTP */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)]">
                    <div className="flex items-center gap-3">
                      <Smartphone className="h-4 w-4 text-[var(--text-secondary)]" />
                      <div>
                        <p className="text-sm font-medium text-[var(--text-primary)]">Authenticator App</p>
                        <p className="text-xs text-[var(--text-secondary)]">
                          {totpFactor ? 'Configured' : 'Not configured'}
                        </p>
                      </div>
                    </div>
                    {totpFactor ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" loading={actionLoading === `mfa-${totpFactor.id}`}>
                            Remove
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove authenticator?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will disable TOTP-based two-factor authentication. You can set it up again later.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleRemoveMFA(totpFactor.id)}>Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={handleBeginTotpSetup}>Set up</Button>
                    )}
                  </div>

                  {/* Passkeys */}
                  <div className="p-3 rounded-lg bg-[var(--bg-elevated)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Key className="h-4 w-4 text-[var(--text-secondary)]" />
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">Passkeys</p>
                          <p className="text-xs text-[var(--text-secondary)]">
                            {passkeys.length > 0 ? `${passkeys.length} configured` : 'Not configured'}
                          </p>
                        </div>
                      </div>
                      <Button variant="secondary" size="sm" onClick={handleBeginPasskeyEnrollment}>
                        Add passkey
                      </Button>
                    </div>

                    {passkeys.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {passkeys.map((passkey) => (
                          <div key={passkey.id} className="flex items-center justify-between p-2 rounded bg-[var(--bg-elevated-2)]">
                            <div>
                              <p className="text-sm text-[var(--text-primary)]">{passkey.name || 'Passkey'}</p>
                              <p className="text-xs text-[var(--text-muted)]">
                                Added {new Date(passkey.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" loading={actionLoading === `mfa-${passkey.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove passkey?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will remove this passkey from your account. You can add it back later.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleRemoveMFA(passkey.id)}>Remove</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recovery Codes */}
                  {hasMFA && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)]">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="h-4 w-4 text-[var(--text-secondary)]" />
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">Recovery Codes</p>
                          <p className="text-xs text-[var(--text-secondary)]">Backup codes for account recovery</p>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setRecoveryModalOpen(true)
                          handleRegenerateRecoveryCodes()
                        }}
                      >
                        View codes
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Linked Accounts */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-[var(--text-secondary)]" />
              <CardTitle>Linked Accounts</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {identities.length > 0 && (
              <div className="space-y-3 mb-4">
                {identities.map((identity) => (
                  <div
                    key={identity.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)]"
                  >
                    <div className="flex items-center gap-3">
                      <SocialProviderIcon provider={identity.provider} />
                      <div>
                        <p className="text-sm font-medium text-[var(--text-primary)] capitalize">{identity.provider}</p>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {identity.email || identity.provider_user_id}
                        </p>
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" loading={actionLoading === `identity-${identity.id}`}>
                          Unlink
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Unlink {identity.provider}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You won't be able to sign in with this {identity.provider} account anymore.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleUnlinkIdentity(identity.id)}>Unlink</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}

            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                {identities.length > 0 ? 'Link another account' : 'Link a social account to enable quick sign-in'}
              </p>
              <div className="flex flex-wrap gap-2">
                {SOCIAL_PROVIDERS.filter(p => !identities.some(i => i.provider === p.id)).map((provider) => (
                  <Button key={provider.id} variant="secondary" size="sm" onClick={() => handleLinkAccount(provider.id)}>
                    <SocialProviderIcon provider={provider.id} className="mr-2" />
                    {provider.name}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-[var(--text-secondary)]" />
              <CardTitle>Active Sessions</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentSession && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)]">
                  <div className="flex items-center gap-3">
                    <Monitor className="h-5 w-5 text-[var(--text-secondary)]" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[var(--text-primary)]">
                          {currentSession.device_name || currentSession.browser || 'Unknown device'}
                        </p>
                        <Badge variant="success" className="text-xs">Current</Badge>
                      </div>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {currentSession.ip_address || 'Unknown IP'} • Active now
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {otherSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)]">
                  <div className="flex items-center gap-3">
                    <Monitor className="h-5 w-5 text-[var(--text-secondary)]" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">
                        {session.device_name || session.browser || 'Unknown device'}
                      </p>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {session.ip_address || 'Unknown IP'} • {formatLastActive(session.last_active_at)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeSession(session.id)}
                    loading={actionLoading === `session-${session.id}`}
                  >
                    Revoke
                  </Button>
                </div>
              ))}

              {otherSessions.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRevokeAllSessions}
                  loading={actionLoading === 'revoke-all'}
                  className="mt-2"
                >
                  Revoke all other sessions
                </Button>
              )}

              {sessions.length === 0 && (
                <p className="text-sm text-[var(--text-secondary)]">No active sessions</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email Change Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Email</DialogTitle>
            <DialogDescription>
              Enter your new email address and current password to verify this change.
              A verification link will be sent to your new email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {emailError && (
              <Alert variant="error" onClose={() => setEmailError(null)}>{emailError}</Alert>
            )}
            <Input
              label="New Email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter your new email"
            />
            <Input
              label="Current Password"
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              placeholder="Enter your password"
            />
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setEmailModalOpen(false)
                setNewEmail('')
                setEmailPassword('')
                setEmailError(null)
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEmailChange} loading={isChangingEmail} disabled={!newEmail || !emailPassword}>
              Send Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {passwordError && (
              <Alert variant="error" onClose={() => setPasswordError(null)}>{passwordError}</Alert>
            )}
            <Input
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setPasswordModalOpen(false)
                setCurrentPassword('')
                setNewPassword('')
                setConfirmPassword('')
                setPasswordError(null)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              loading={isChangingPassword}
              disabled={!currentPassword || !newPassword || !confirmPassword}
            >
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TOTP Setup Modal */}
      <Dialog open={totpModalOpen} onOpenChange={(open) => {
        if (!open) {
          setTotpSetup(null)
          setTotpCode('')
          setTotpError(null)
        }
        setTotpModalOpen(open)
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set up Authenticator App</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isSettingUpTotp ? (
              <div className="text-center py-8">
                <p className="text-[var(--text-secondary)]">Loading...</p>
              </div>
            ) : totpError && !totpSetup ? (
              <Alert variant="error" onClose={() => setTotpError(null)}>{totpError}</Alert>
            ) : totpSetup ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <img
                    src={totpSetup.qr_code_base64 ? `data:image/png;base64,${totpSetup.qr_code_base64}` : ''}
                    alt="TOTP QR Code"
                    className="w-48 h-48 rounded-lg border border-[var(--border-subtle)]"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Or enter this code manually:</p>
                  <code className="text-sm font-mono bg-[var(--bg-elevated-2)] px-3 py-1 rounded select-all">
                    {totpSetup.secret}
                  </code>
                </div>
                <div className="pt-4">
                  {totpError && (
                    <Alert variant="error" className="mb-4" onClose={() => setTotpError(null)}>{totpError}</Alert>
                  )}
                  <Input
                    label="Verification Code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit code"
                  />
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTotpModalOpen(false)}>Cancel</Button>
            <Button onClick={handleVerifyTotp} loading={isVerifyingTotp} disabled={!totpSetup || totpCode.length !== 6}>
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Passkey Enrollment Modal */}
      <Dialog open={passkeyModalOpen} onOpenChange={(open) => {
        if (!open) {
          setPasskeyName('')
          setPasskeyError(null)
        }
        setPasskeyModalOpen(open)
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add a Passkey</DialogTitle>
            <DialogDescription>
              Use Face ID, Touch ID, Windows Hello, or a security key to sign in securely without a password.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {passkeyError && (
              <Alert variant="error" onClose={() => setPasskeyError(null)}>{passkeyError}</Alert>
            )}
            <Input
              label="Passkey Name (optional)"
              type="text"
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
              placeholder="e.g., MacBook Pro, iPhone"
            />
            <p className="text-xs text-[var(--text-muted)]">
              Give your passkey a name to help identify it later.
            </p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasskeyModalOpen(false)}>Cancel</Button>
            <Button onClick={handleEnrollPasskey} loading={isEnrollingPasskey}>
              <Fingerprint className="mr-2 h-4 w-4" />
              Create Passkey
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recovery Codes Modal */}
      <Dialog open={recoveryModalOpen} onOpenChange={setRecoveryModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recovery Codes</DialogTitle>
            <DialogDescription>
              Save these codes in a safe place. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isLoadingCodes ? (
              <div className="text-center py-8">
                <p className="text-[var(--text-secondary)]">Loading codes...</p>
              </div>
            ) : recoveryCodes.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 p-4 bg-[var(--bg-elevated-2)] rounded-lg font-mono text-sm">
                  {recoveryCodes.map((code, index) => (
                    <div key={index} className="text-[var(--text-primary)]">{code}</div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={copyRecoveryCodes} className="flex-1">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button variant="secondary" size="sm" onClick={downloadRecoveryCodes} className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-center text-[var(--text-secondary)]">No codes available</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setRecoveryModalOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function formatLastActive(dateString: string | null): string {
  if (!dateString) return 'Unknown'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 5) return 'Active now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}

// WebAuthn helper functions
function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padLen = (4 - (base64.length % 4)) % 4
  const padded = base64 + '='.repeat(padLen)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
